/**
 * platform-lab TanStack Query v5 훅 모음
 *
 * queryKey 패턴: ['platform-lab', '<tab>', ...params]
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { platformLabApi } from '../api/platformLabApi';
import type {
  IdGenRequest,
  IdKind,
  AsyncSubmitRequest,
  ResilienceRequest,
  SessionAttributeRequest,
} from '../types/platformLab';

// ── 버전 탭 (C025) ──────────────────────────────────────────────────────

/**
 * v1 echo 뮤테이션 — 버튼 클릭 시 호출
 * (응답 헤더 Deprecation/Sunset/Link 포함)
 */
export function useEchoV1() {
  return useMutation({
    mutationFn: (msg: string) => platformLabApi.echoV1(msg),
  });
}

/** v2 echo 뮤테이션 */
export function useEchoV2() {
  return useMutation({
    mutationFn: (msg: string) => platformLabApi.echoV2(msg),
  });
}

// ── ID 생성 탭 (C023) ───────────────────────────────────────────────────

/** POST /platform-lab/ids — 5종 ID 생성 */
export function useGenerateIds() {
  return useMutation({
    mutationFn: (request: IdGenRequest) => platformLabApi.generateIds(request),
  });
}

/** GET /platform-lab/ids/batch — Snowflake 배치 생성 */
export function useIdBatch() {
  return useMutation({
    mutationFn: ({ kind, count }: { kind: IdKind; count: number }) =>
      platformLabApi.batchIds(kind, count),
  });
}

// ── 비동기 탭 (C019) ────────────────────────────────────────────────────

/** POST /platform-lab/async/submit — 작업 제출 */
export function useSubmitAsyncTask() {
  return useMutation({
    mutationFn: (request: AsyncSubmitRequest) => platformLabApi.submitTask(request),
  });
}

/** GET /platform-lab/async/tasks/{taskId} — 1s 간격 polling (enabled=taskId 존재) */
export function useAsyncTaskResult(taskId: string | null) {
  return useQuery({
    queryKey: ['platform-lab', 'async', 'task', taskId],
    queryFn: () => platformLabApi.getTaskResult(taskId!),
    enabled: !!taskId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      // DONE/FAILED 이면 polling 중지
      if (status === 'DONE' || status === 'FAILED') return false;
      return 1000;
    },
    staleTime: 0,
  });
}

// ── 복원력 탭 (C018) ────────────────────────────────────────────────────

/** POST /platform-lab/resilience/circuit-breaker */
export function useResilienceCb() {
  return useMutation({
    mutationFn: (request: ResilienceRequest) => platformLabApi.circuitBreaker(request),
  });
}

/** POST /platform-lab/resilience/retry */
export function useResilienceRetry() {
  return useMutation({
    mutationFn: (request: ResilienceRequest) => platformLabApi.retry(request),
  });
}

/** POST /platform-lab/resilience/timeout */
export function useResilienceTimeout() {
  return useMutation({
    mutationFn: (request: ResilienceRequest) => platformLabApi.timeout(request),
  });
}

/** 비동기 작업 결과 캐시 무효화 헬퍼 */
export function useInvalidateAsyncTask() {
  const qc = useQueryClient();
  return (taskId: string) =>
    qc.invalidateQueries({ queryKey: ['platform-lab', 'async', 'task', taskId] });
}

// ── 분산 세션 탭 (C036) ─────────────────────────────────────────────────

/** POST /platform-lab/session/attributes — 속성 저장 */
export function useSaveSessionAttribute() {
  return useMutation({
    mutationFn: (request: SessionAttributeRequest) => platformLabApi.saveSessionAttribute(request),
  });
}

/** GET /platform-lab/session — 현재 세션 조회 (버튼 트리거) */
export function useSessionInfo() {
  return useMutation({
    mutationFn: () => platformLabApi.getSessionInfo(),
  });
}

/** DELETE /platform-lab/session — 세션 무효화 (JWT 로그인은 불변) */
export function useInvalidateSession() {
  return useMutation({
    mutationFn: () => platformLabApi.invalidateSession(),
  });
}

/** GET /platform-lab/session/redis-keys — 분산 저장 가시화 (수동 refetch) */
export function useSessionRedisKeys() {
  return useQuery({
    queryKey: ['platform-lab', 'session', 'redis-keys'],
    queryFn: () => platformLabApi.getSessionRedisKeys(),
    enabled: false,
    staleTime: 0,
  });
}
